import './dasher_slideshow.less';

import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import findIndex from 'lodash/findIndex';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';

import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Carousel } from 'react-responsive-carousel';

import {
  getSlideNavigationArrows,
  slideGenerator,
  thumbnailsExtractor,
} from './helpers.js';

import { slideSpeedIntervals, BASE_SLIDE_SHOW_HEIGHT, SLIDESHOW_CONST } from './settings.js';

import { VideoPlayIcon } from 'zoomforth_component_lib/app_modern_icons.jsx';
import { PlayIcon, PauseIcon } from 'zoomforth_component_lib/external_modern_icons.jsx';
import CircularSpinner from 'zoomforth_component_lib/circular_spinner.jsx';

import { VisuallyHiddenSpan } from 'zoomforth_codesbane_viewer/component_lib/visually_hidden.jsx';


const calcSlideHeight = (slideHeight) => {
  // converting string to number values, to facilitate calculations and converting
  // all non pixel values to  pixel to facilitate calculations regarding the slide sizing
  let parsedHeight;
  if (slideHeight.includes(SLIDESHOW_CONST.EM)) {
    parsedHeight = Number(slideHeight.replace(SLIDESHOW_CONST.EM, '')) * 16;
  }

  if (slideHeight.includes(SLIDESHOW_CONST.PX)) {
    parsedHeight = Number(slideHeight.replace(SLIDESHOW_CONST.PX, ''));
  }

  if (slideHeight.includes(SLIDESHOW_CONST.VH)) {
    parsedHeight = Math.round(
      window.innerHeight /
        (100 / Number(slideHeight.replace(SLIDESHOW_CONST.VH, '')))
    );
  }

  return parsedHeight;
};

const DasherSlideshow = ({
  slideHeight = BASE_SLIDE_SHOW_HEIGHT + SLIDESHOW_CONST.PX,
  contentItemAssetPairs,
  ariaLabel = SLIDESHOW_CONST.SLIDESHOW_ARIA_LABEL,
  labels = {},
  ...props
}) => {
  const {
    isMobile,
    isAutoplaySupported,
    section,
    selectedContentItem,
    onOverlayOpen,
    onSelectContentClick,
    onContentChange
  } = props;

  const {
    slideAnimationSpeed,
    slideArrowType,
    slideArrowsColor,
    slideAutoplay,
    slideSpotlight,
    slideshowNav = SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.THUMBNAILS,
    slideLoopAnimation,
  } = section;

  const currentSlideUuid = selectedContentItem.uuid;

  const thumbnailsSet = thumbnailsExtractor(contentItemAssetPairs);

  const currentSlideIndex = findIndex(thumbnailsSet, function (o) {
    return o.uuid == currentSlideUuid;
  });

  const navigationThumbnailSet = thumbnailsSet.map((thumbnail, idx) => {
    return (
      <div key={idx}>
        {thumbnail.isVideo && (
          <div className="thumbnail-video-icon">
            <VideoPlayIcon style={{ color: 'white', opacity: 0.8 }} />
          </div>
        )}
        {isNil(thumbnail.uuid) ? (
          <div className="slide-spinner-container"><CircularSpinner /></div>
        ) : (
          <img className={thumbnail.isVideo ? 'video-icon' : null} alt={thumbnail.altText} src={thumbnail.thumbnailUrl} />
        )}
      </div>
    );
  });

  const hasMoreThanOneSlide = contentItemAssetPairs.length > 1;
  const shouldShowNavigation =
    hasMoreThanOneSlide ||
    (hasMoreThanOneSlide &&
      slideshowNav === SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.DOTS) ||
    (hasMoreThanOneSlide &&
      slideshowNav === SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.THUMBNAILS);

  const SLIDE_NAVIGATION_CONTAINER_HEIGHT = 75;

  // calculating the height of the outer slide container
  // in case we want to display the navigation we need to add space for the navigation area
  // this is done in the outer container, so that calculating the inner slide dimensions for inclusion of the captions
  // as well as image mode display (cover, contain) is applied correctly
  const outerSlideHolder = shouldShowNavigation
    ? calcSlideHeight(slideHeight) + SLIDE_NAVIGATION_CONTAINER_HEIGHT + SLIDESHOW_CONST.PX
    : calcSlideHeight(slideHeight) + SLIDESHOW_CONST.PX;
  const calculatedSlideHeight = calcSlideHeight(slideHeight) + SLIDESHOW_CONST.PX;

  const [currentItem, setCurrentItem] = useState(null);

  const [didVisitorStopAutoplay, setDidVisitorStopAutoplay] = useState(false);
  const isAutoplayActive = (
    !isAutoplaySupported ?
      false
    : didVisitorStopAutoplay ?
      false
    :
      slideAutoplay
  );

  return (
    <div style={{ position: 'relative' }}>
      {
        slideAutoplay &&
          <div className="carousel-autoplay-controls">
            <button
              className="playpause-btn"
              onClick={()=> {
                setDidVisitorStopAutoplay(!didVisitorStopAutoplay);
              }}
            >
              { isAutoplayActive ?  <PauseIcon /> : <PlayIcon /> } Slideshow
              <VisuallyHiddenSpan>
                { isAutoplayActive ?  'Stop autoplay' : 'Start autoplay' }
              </VisuallyHiddenSpan>
            </button>
          </div>
      }

      {isMobile ? (
        <Carousel
          labels={labels}
          ariaLabel={ariaLabel}
          infiniteLoop={slideLoopAnimation}
          className={classNames('slideMasterHeight')}
          showArrows={true}
          showIndicators={hasMoreThanOneSlide && slideshowNav === SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.DOTS}
          showThumbs={hasMoreThanOneSlide && slideshowNav === SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.THUMBNAILS}
          autoPlay={isAutoplayActive}
          thumbnailsSet={navigationThumbnailSet}
          shouldExpandSlide={false}
          interval={slideSpeedIntervals[`${slideAnimationSpeed}`]}
          selectedItem={currentSlideIndex || 0}
          // allows user to navigate slideshow using arrows
          useKeyboardArrows={true}
          slideHeight={calcSlideHeight(slideHeight)}
          slideContainerHeight={outerSlideHolder}
          renderArrowPrev={(onClickHandler, hasPrev, label) =>
            hasPrev && (
              <button
                tabIndex={0}
                className={classNames(
                  'slideshow-arrow',
                  'slideshow-arrow-previous',
                  {
                    'light-arrow-color':slideArrowsColor ===SLIDESHOW_CONST.SLIDESHOW_ARROW_COLOR.LIGHT,
                    'dark-arrow-color':slideArrowsColor ===SLIDESHOW_CONST.SLIDESHOW_ARROW_COLOR.DARK,
                    'arrow-hidden':slideArrowType === SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.HIDE,
                  }
                )}
                type="button"
                onClick={onClickHandler}
                title={label}
              >
                <span className="slidenav-arrow">{SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_DIRECTION_LABELS.PREVIOUS}</span>
                {getSlideNavigationArrows(slideArrowType, SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.LEFT)}
              </button>
            )
          }
          renderArrowNext={(onClickHandler, hasNext, label) =>
            hasNext && (
              <button
                tabIndex={0}
                type="button"
                className={classNames(
                  'slideshow-arrow',
                  'slideshow-arrow-next',
                  {
                    'light-arrow-color':slideArrowsColor ===SLIDESHOW_CONST.SLIDESHOW_ARROW_COLOR.LIGHT,
                    'dark-arrow-color':slideArrowsColor ===SLIDESHOW_CONST.SLIDESHOW_ARROW_COLOR.DARK,
                    'arrow-hidden':slideArrowType === SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.HIDE,
                  }
                )}
                onClick={onClickHandler}
                title={label}
              >
                <span className="slidenav-arrow">{SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_DIRECTION_LABELS.NEXT}</span>
                {getSlideNavigationArrows(
                  slideArrowType,
                  SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.RIGHT
                )}
              </button>
            )
          }
          centerMode={false /* center mode is busted on mobile */}
          // how much emphasis is given the center slide
          centerSlidePercentage={100}
          // displays a status message in the base component, which is always displayed
          // as we don't need it, we are muting it.
          showStatus={false}
          // DO NOT DELETE!!!!!!!!!!!!!!!!
          swipeable={true}
          emulateTouch={true}
        >
          {slideGenerator(
            contentItemAssetPairs,
            calculatedSlideHeight,
            onOverlayOpen,
            section,
            isMobile
          )}


        </Carousel>
      ) : (
        <Carousel
          labels={labels}
          ariaLabel={ariaLabel}
          infiniteLoop={slideLoopAnimation}
          className={classNames('slideMasterHeight')}
          showIndicators={hasMoreThanOneSlide && slideshowNav === SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.DOTS}
          showThumbs={hasMoreThanOneSlide && slideshowNav === SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.THUMBNAILS}
          autoPlay={isAutoplayActive}
          thumbnailsSet={navigationThumbnailSet}
          shouldExpandSlide={false}
          interval={slideSpeedIntervals[`${slideAnimationSpeed}`]}
          selectedItem={currentSlideIndex || 0}
          // allows user to navigate slideshow using arrows
          useKeyboardArrows={true}
          slideHeight={calcSlideHeight(slideHeight)}
          slideContainerHeight={outerSlideHolder}
          renderArrowPrev={(onClickHandler, hasPrev, label) =>
            hasPrev && (
              <button
                tabIndex={0}
                className={classNames(
                  'slideshow-arrow',
                  'slideshow-arrow-previous',
                  {
                    'light-arrow-color':slideArrowsColor === SLIDESHOW_CONST.SLIDESHOW_ARROW_COLOR.LIGHT,
                    'dark-arrow-color': slideArrowsColor === SLIDESHOW_CONST.SLIDESHOW_ARROW_COLOR.DARK,
                    'arrow-hidden':slideArrowType === SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.HIDE,
                  }
                )}
                type="button"
                onClick={onClickHandler}
                title={label}
              >
                <span className="slidenav-arrow">{SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_DIRECTION_LABELS.PREVIOUS}</span>
                {getSlideNavigationArrows(
                  slideArrowType,
                  SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.LEFT
                )}
              </button>
            )
          }
          renderArrowNext={(onClickHandler, hasNext, label) =>
            hasNext && (
              <button
                tabIndex={0}
                type="button"
                className={classNames(
                  'slideshow-arrow',
                  'slideshow-arrow-next',
                  {
                    'light-arrow-color':slideArrowsColor ===SLIDESHOW_CONST.SLIDESHOW_ARROW_COLOR.LIGHT,
                    'dark-arrow-color':slideArrowsColor ===SLIDESHOW_CONST.SLIDESHOW_ARROW_COLOR.DARK,
                    'arrow-hidden':slideArrowType === SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.HIDE,
                  }
                )}
                onClick={onClickHandler}
                title={label}
              >
                <span className="slidenav-arrow">{SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_DIRECTION_LABELS.NEXT}</span>
                {getSlideNavigationArrows(
                  slideArrowType,
                  SLIDESHOW_CONST.SLIDESHOW_ARROW_TYPE.RIGHT
                )}
              </button>
            )
          }
          centerMode={slideSpotlight === 'center'}
          // how much emphasis is given the center slide
          centerSlidePercentage={80}
          // displays a status message in the base component, which is always displayed
          // as we don't need it, we are muting it.
          showStatus={false}
          swipeable={true}
          emulateTouch={true}
          onClickItem={(index, item)=> {
            const { contentItem } = contentItemAssetPairs[index] || {};

            onSelectContentClick(contentItem);

            setCurrentItem(index);
          }}
          onChange={(index, item) => {
            const { contentItem } = contentItemAssetPairs[index] || {};

            onContentChange(contentItem);

            setCurrentItem(index);
          }}
        >
          {slideGenerator(
            contentItemAssetPairs,
            calculatedSlideHeight,
            onOverlayOpen,
            section,
            false,
            currentItem
          )}

        </Carousel>
      )}
    </div>
  );
};

// restructure these guys
DasherSlideshow.propTypes = {
  /**
   * This allows us to control whether autoplay happens at all. For example we
   * don't want to have it autoplay in the editor.
   */
  isAutoplaySupported: PropTypes.bool.isRequired,
  /**
   * The height of the slideshow componet. Set by default to 485, can however be
   * redefined when the user chooses a height from the editor pane
   */
  height: PropTypes.number,
  // contentItemAssetPairs,
  /**
   * Navigation arrows that can be used to navigate the slide items
   */
  slideArrowType: PropTypes.oneOf(['arrow', 'chevron', 'circular']),
  /**
   * Define the aria-label attribute for the root carousel element. The default is undefined, skipping the attribute from markup.
   */
  ariaLabel: PropTypes.string,
  /**
   * Whether the component cycles back to the first item, once it ends the current play cycle.
   */
  infiniteLoop: PropTypes.bool,
  /**
   * Apply aria-label on carousel with an object with the properties leftArrow, rightArrow and item.
   * he default is {leftArrow: 'previous slide / item', rightArrow: 'next slide / item', item: 'slide item'}.
   */
  labels: PropTypes.shape({
    leftArrow: PropTypes.string,
    rightArrow: PropTypes.string,
  }),
  /**
   * Whether the slideshow is in ayutoplay mode
   */
  slideAutoplay: PropTypes.bool,
  /**
   * The speed with which the slideshow autoplays. By default set to normal.
   * Only has effect if autoplay is set to true
   * The speeds in seconds are 5, 4, 3, 2 and 1 respectively.
   */
  slideshowSpeed: PropTypes.oneOf([
    SLIDESHOW_CONST.SLIDESHOW_PLAYBACK_SPEED.SLOWER,
    SLIDESHOW_CONST.SLIDESHOW_PLAYBACK_SPEED.SLOW,
    SLIDESHOW_CONST.SLIDESHOW_PLAYBACK_SPEED.NORMAL,
    SLIDESHOW_CONST.SLIDESHOW_PLAYBACK_SPEED.FAST,
    SLIDESHOW_CONST.SLIDESHOW_PLAYBACK_SPEED.FASTER,
  ]),
  /**
   * The emphasis given the slides are presented in a viewer
   */
  slideSpotlight: PropTypes.oneOf([
    SLIDESHOW_CONST.SLIDESHOW_SPOTLIGHT_TYPE.SINGLE,
    SLIDESHOW_CONST.SLIDESHOW_SPOTLIGHT_TYPE.SLIDE,
    SLIDESHOW_CONST.SLIDESHOW_SPOTLIGHT_TYPE.CENTER,
  ]),
  /**
   * Slideshow navigation type
   */
  slideshowNav: PropTypes.oneOf([
    SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.THUMBNAILS,
    SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.DOTS,
    SLIDESHOW_CONST.SLIDESHOW_NAVIGATION_LABELS.NONE,
  ]),
  /**
   * Whether we want to display a caption associated with a given slide
   */
  showCaption: PropTypes.bool,
  /**
   * Where the caption should be positioned within the slide
   */
  captionPosition: PropTypes.oneOf([
    SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.LEFT,
    SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.RIGHT,
    SLIDESHOW_CONST.SLIDESHOW_CAPTION_POSITION.BOTTOM,
  ]),
  captionTextAlignment: PropTypes.oneOf([
    SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_ALIGNMENT.LEFT,
    SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_ALIGNMENT.CENTER,
    SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_ALIGNMENT.RIGHT,
  ]),
  captionTextPosition: PropTypes.oneOf([
    SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_POSITION.TOP,
    SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_POSITION.MIDDLE,
    SLIDESHOW_CONST.SLIDESHOW_CAPTION_TEXT_POSITION.BOTTOM,
  ]),
};

DasherSlideshow.defaultProps = {
  isAutoplaySupported: true,
  onContentChange: noop
};

export default DasherSlideshow;
